/**
 * Szállítólevelek oldal — lista + megrendelésből szállítólevél (tétel-kiválasztással)
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { get, post, downloadFile } from '../../lib/api';
import { PageHeader, Button, Badge, Card, Input, Modal, EmptyState, LoadingOverlay, NumInput } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';
import ActionsDropdown from '../../components/ActionsDropdown';
import PostIssueModal from '../../components/PostIssueModal';
import { useFKeyBar } from '../../components/layout/FKeyBar';

interface DnLine {
  id: string;
  item_name_snapshot: string;
  item_sku_snapshot?: string;
  quantity: number;
  unit: string;
  konfig_params_display?: { nev: string; ertek: string | number }[];
}

interface DeliveryNote {
  id: string;
  delivery_note_number: string;
  partner_name?: string;
  delivery_date: string;
  status: string;
  notes?: string;
  lines?: DnLine[];
  storno_of_id?: string;
  storno_of_number?: string;
  storno_child_id?: string;
  storno_child_number?: string;
  storno_reason?: string;
  issued_at?: string;
  show_prices?: boolean;
}

const STATUS_MAP: Record<string, { label: string; variant: 'default' | 'info' | 'success' | 'error' | 'warning' }> = {
  draft: { label: 'Vázlat', variant: 'default' },
  issued: { label: 'Kiállítva', variant: 'info' },
  received: { label: 'Átvéve', variant: 'success' },
  cancelled: { label: 'Törölve', variant: 'error' },
  storno: { label: 'Sztornózva', variant: 'warning' },
};

function fmtDate(iso: string) {
  return iso ? new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
}

function fmtQty(n: number) {
  if (Number.isInteger(n)) return n.toLocaleString('hu-HU');
  return n.toLocaleString('hu-HU', { minimumFractionDigits: 1, maximumFractionDigits: 4 });
}

export default function DeliveryNotesPage() {
  const { toast } = useToast();
  const [notes, setNotes] = useState<DeliveryNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [showDetail, setShowDetail] = useState<DeliveryNote | null>(null);

  useFKeyBar([
    { key: 'F5', label: 'Új szállítólevél', action: () => setShowEditor(true) },
    { key: 'F6', label: showDetail ? 'Bezárás' : 'Szállítólevél', action: () => { if (showDetail) setShowDetail(null); } },
    { key: 'F8', label: 'PDF letöltés', action: () => { if (showDetail) downloadFile(`/delivery-notes/${showDetail.id}/pdf`); } },
  ]);
  const [detailLines, setDetailLines] = useState<DnLine[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [postIssue, setPostIssue] = useState<{ id: string; number: string; partnerId: string; partnerName: string } | null>(null);
  const [globalLoading, setGlobalLoading] = useState(false);

  // Date filter state — default: current month
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0,10));

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Highlight
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  async function loadNotes() {
    setLoading(true);
    try {
      const data = await get<any>('/delivery-notes');
      setNotes(Array.isArray(data) ? data : data?.data || []);
    } catch { /* */ }
    setLoading(false);
  }

  useEffect(() => { loadNotes(); }, []);

  // Handle URL highlight param on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const highlight = params.get('highlight');
    if (highlight) {
      setHighlightId(highlight);
      setDateFrom('');
      setDateTo('');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Scroll to highlighted card after data loads
  useEffect(() => {
    if (!highlightId || loading) return;
    const el = cardRefs.current[highlightId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.style.animation = 'highlightPulse 0.6s ease-in-out 3';
      const t = setTimeout(() => {
        if (el) el.style.animation = '';
        setHighlightId(null);
      }, 2000);
      return () => clearTimeout(t);
    }
  }, [highlightId, loading, notes]);

  async function openDetail(dn: DeliveryNote) {
    try {
      const full = await get<any>(`/delivery-notes/${dn.id}`);
      setShowDetail(full);
      setDetailLines(full.lines || []);
    } catch {
      setShowDetail(dn);
      setDetailLines([]);
    }
  }

  const filtered = useMemo(() => {
    let list = notes.filter(n => {
      if (statusFilter === 'storno') {
        if (n.status !== 'storno' && !n.storno_of_id) return false;
      } else if (statusFilter && n.status !== statusFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!(n.delivery_note_number || '').toLowerCase().includes(s) && !(n.partner_name || '').toLowerCase().includes(s)) return false;
      }
      return true;
    });
    if (dateFrom) list = list.filter(n => (n.delivery_date || '') >= dateFrom);
    if (dateTo) list = list.filter(n => (n.delivery_date || '') <= dateTo);
    return list;
  }, [notes, statusFilter, search, dateFrom, dateTo]);

  // Reset page on filter change
  useEffect(() => { setPage(1); }, [statusFilter, search, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  const counts = {
    all: notes.length,
    draft: notes.filter(n => n.status === 'draft').length,
    issued: notes.filter(n => n.status === 'issued').length,
    storno: notes.filter(n => n.status === 'storno' || !!n.storno_of_id).length,
  };

  return (
    <div>
      <LoadingOverlay visible={globalLoading} text="PDF előkészítése..." />
      <PageHeader title="Szállítólevelek" description={`${paginated.length}/${filtered.length} szállítólevél`}
        actions={<Button onClick={() => setShowEditor(true)}>+ Új szállítólevél</Button>} />

      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <Input placeholder="Keresés sorszám, partner..." value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, minWidth: '200px' }} />
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="form-input" style={{ width: '140px', fontSize: '13px' }} title="Dátumtól" />
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="form-input" style={{ width: '140px', fontSize: '13px' }} title="Dátumig" />
        <div style={{ display: 'flex', gap: '4px' }}>
          <button onClick={() => { const d = new Date(); setDateFrom(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`); setDateTo(d.toISOString().slice(0,10)); }} style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)', cursor: 'pointer', background: 'var(--white)', color: 'var(--ink)' }}>Ez a hónap</button>
          <button onClick={() => { const d = new Date(); d.setMonth(d.getMonth()-1); setDateFrom(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`); const last = new Date(d.getFullYear(), d.getMonth()+1, 0); setDateTo(last.toISOString().slice(0,10)); }} style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)', cursor: 'pointer', background: 'var(--white)', color: 'var(--ink)' }}>Előző hónap</button>
          <button onClick={() => { setDateFrom(''); setDateTo(''); }} style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)', cursor: 'pointer', background: 'var(--white)', color: 'var(--ink)' }}>Mind</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '14px', flexWrap: 'wrap' }}>
        {[
          { key: '', label: `Mind (${counts.all})` },
          { key: 'issued', label: `Kiállítva (${counts.issued})` },
          ...(counts.storno > 0 ? [{ key: 'storno', label: `Sztornó (${counts.storno})` }] : []),
          ...(counts.draft > 0 ? [{ key: 'draft', label: `Vázlat (${counts.draft})` }] : []),
        ].map(f => (
          <button key={f.key} onClick={() => setStatusFilter(f.key)}
            style={{ padding: '4px 10px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)',
              background: statusFilter === f.key ? 'var(--navy)' : 'var(--white)', color: statusFilter === f.key ? '#fff' : 'var(--ink)',
              cursor: 'pointer', fontWeight: statusFilter === f.key ? 600 : 400 }}>{f.label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--slate)' }}>Betöltés...</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon="📦" title="Még nincsenek szállítólevelek"
          description="Szállítólevelet a Megrendelések oldalon tudsz létrehozni, vagy itt az Új szállítólevél gombbal."
          action={<Button size="sm" onClick={() => setShowEditor(true)}>+ Új szállítólevél</Button>} />
      ) : (
        <div style={{ display: 'grid', gap: '10px' }}>
          {paginated.map(n => {
            const st = STATUS_MAP[n.status] || STATUS_MAP.draft;
            const isStornoDoc = !!n.storno_of_id;
            const isStornoed = n.status === 'storno';
            const isWithin20 = n.issued_at && (Date.now() - new Date(n.issued_at).getTime()) < 20 * 60 * 1000;

            // Ikon
            let icon = '📦';
            if (isStornoDoc) icon = '↩️';
            else if (isStornoed) icon = '❌';
            else if (n.status === 'cancelled') icon = '🗑️';
            else if (n.show_prices) icon = '📦💰';

            return (
              <div key={n.id} data-id={n.id} ref={(el) => { cardRefs.current[n.id] = el; }} style={{ borderRadius: 'var(--r)' }}>
              <Card style={{
                cursor: 'pointer',
                borderLeft: isStornoed ? '3px solid var(--red)' : isStornoDoc ? '3px solid #f59e0b' : undefined,
                opacity: n.status === 'cancelled' ? 0.5 : 1,
              }} onClick={() => openDetail(n)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: isStornoed ? 'var(--red)' : 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.3px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '16px' }}>{icon}</span>
                      <span style={{ textDecoration: isStornoed ? 'line-through' : undefined }}>{n.delivery_note_number}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '2px' }}>{n.partner_name || '—'}</div>

                    {/* Sztornó referenciák */}
                    {isStornoDoc && n.storno_of_number && (
                      <div style={{ fontSize: '11px', marginTop: '3px', color: '#d97706', fontWeight: 600 }}>
                        ↩️ {n.storno_of_number.toUpperCase()} sztornó szállítólevele
                      </div>
                    )}
                    {isStornoed && n.storno_child_number && (
                      <div style={{ fontSize: '11px', marginTop: '3px' }} onClick={e => e.stopPropagation()}>
                        <span style={{ color: 'var(--red)' }}>❌ Sztornózva: </span>
                        <button onClick={() => { const linked = notes.find(x => x.id === n.storno_child_id); if (linked) openDetail(linked); }}
                          style={{ color: '#d97706', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: '11px', textTransform: 'uppercase' }}>
                          {n.storno_child_number}
                        </button>
                      </div>
                    )}
                    {n.storno_reason && (
                      <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '2px', fontStyle: 'italic' }}>
                        Ok: {n.storno_reason}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} onClick={e => e.stopPropagation()}>
                    <Badge variant={st.variant}>{st.label}</Badge>
                    {n.show_prices && n.status === 'issued' && <Badge variant="default">Áras</Badge>}
                    <ActionsDropdown actions={[
                      { label: 'Részletek', icon: '👁', onClick: () => openDetail(n) },
                      { label: 'PDF letöltés', icon: '📥', onClick: async () => { setGlobalLoading(true); try { await downloadFile(`/delivery-notes/${n.id}/pdf`, `${n.delivery_note_number}.pdf`); } finally { setGlobalLoading(false); } } },
                      ...(n.status === 'issued' && !isStornoDoc ? [
                        ...(isWithin20 ? [{ label: 'Törlés (20 percen belül)', icon: '🗑', variant: 'danger' as const, onClick: async () => {
                          if (!confirm('Biztosan törlöd a szállítólevelet?')) return;
                          try { await post(`/delivery-notes/${n.id}/cancel`, {}); toast('Szállítólevél törölve.', 'success'); loadNotes(); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
                        }}] : []),
                        { label: 'Sztornó szállítólevél', icon: '↩', onClick: async () => {
                          const reason = prompt('Sztornó oka (opcionális):');
                          if (reason === null) return;
                          try { await post(`/delivery-notes/${n.id}/storno`, { reason: reason || undefined }); toast('Sztornó szállítólevél létrehozva.', 'success'); loadNotes(); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
                        }},
                      ] : []),
                      ...(n.status === 'draft' ? [
                        { label: 'Kiállítás', icon: '✅', onClick: async () => {
                          try { await post(`/delivery-notes/${n.id}/issue`, {}); toast('Kiállítva.', 'success'); loadNotes(); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
                        }},
                      ] : []),
                    ]} />
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '12px', color: 'var(--slate)' }}>
                  <span>Dátum: {fmtDate(n.delivery_date)}</span>
                  {n.issued_at && <span>Kiállítva: {fmtDate(n.issued_at)}</span>}
                </div>
              </Card>
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1, background: 'var(--white)' }}>← Előző</button>
          <span style={{ fontSize: '13px', color: 'var(--slate)' }}>{page} / {totalPages}</span>
          <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--line)', borderRadius: 'var(--r)', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.5 : 1, background: 'var(--white)' }}>Következő →</button>
          <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} className="form-input" style={{ width: '70px', fontSize: '12px' }}>
            <option value="10">10</option><option value="25">25</option><option value="50">50</option><option value="100">100</option>
          </select>
        </div>
      )}

      {showEditor && <DnFromOrderModal onClose={() => setShowEditor(false)} onSaved={(issued) => {
        setShowEditor(false); loadNotes();
        if (issued) setPostIssue(issued);
      }} />}

      {postIssue && (
        <PostIssueModal
          isOpen={true}
          onClose={() => setPostIssue(null)}
          docType="delivery-note"
          docId={postIssue.id}
          docNumber={postIssue.number}
          partnerId={postIssue.partnerId}
          partnerName={postIssue.partnerName}
        />
      )}

      {showDetail && (
        <Modal isOpen={true} onClose={() => setShowDetail(null)} title={`Szállítólevél: ${showDetail.delivery_note_number.toUpperCase()}`}>
          <div className="space-y-4">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '11px', color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>Partner</div>
                <div style={{ fontWeight: 600 }}>{showDetail.partner_name || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>Szállítás dátuma</div>
                <div style={{ fontWeight: 600 }}>{fmtDate(showDetail.delivery_date)}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' }}>Státusz</div>
                <Badge variant={(STATUS_MAP[showDetail.status] || STATUS_MAP.draft).variant}>
                  {(STATUS_MAP[showDetail.status] || STATUS_MAP.draft).label}
                </Badge>
              </div>
            </div>

            {detailLines.length > 0 && (
              <div>
                <div style={{ fontSize: '11px', color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Tételek</div>
                <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead><tr style={{ background: 'var(--mist)' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: '11px', color: 'var(--slate)' }}>Megnevezés</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, fontSize: '11px', color: 'var(--slate)' }}>Mennyiség</th>
                      <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 600, fontSize: '11px', color: 'var(--slate)' }}>Egység</th>
                    </tr></thead>
                    <tbody>
                      {detailLines.map((l, i) => (
                        <tr key={l.id || i} style={{ borderTop: '1px solid var(--line-2)' }}>
                          <td style={{ padding: '8px 10px', fontWeight: 500 }}>
                            {l.item_name_snapshot}
                            {l.konfig_params_display && l.konfig_params_display.length > 0 && (
                              <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '3px', display: 'flex', flexWrap: 'wrap', gap: '4px 10px' }}>
                                {l.konfig_params_display.map((p, pi) => (
                                  <span key={pi}><span style={{ fontWeight: 500 }}>{p.nev}:</span> {p.ertek}</span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{fmtQty(Number(l.quantity))}</td>
                          <td style={{ padding: '8px 10px', textAlign: 'center', color: 'var(--slate)' }}>{l.unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {showDetail.notes && (
              <div>
                <div style={{ fontSize: '11px', color: 'var(--slate)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Megjegyzés</div>
                <div style={{ fontSize: '13px', background: 'var(--mist)', padding: '8px 12px', borderRadius: 'var(--r)' }}>{showDetail.notes}</div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '9px', paddingTop: '8px', borderTop: '1px solid var(--line)' }}>
              <Button variant="ghost" onClick={() => downloadFile(`/delivery-notes/${showDetail.id}/pdf`, `${showDetail.delivery_note_number}.pdf`)}>PDF letöltés</Button>
              {showDetail.status === 'issued' && (
                <Button variant="ghost" style={{ color: 'var(--red)' }} onClick={async () => {
                  const reason = prompt('Sztornó oka (opcionális):');
                  if (reason === null) return;
                  try { await post(`/delivery-notes/${showDetail.id}/storno`, { reason: reason || undefined }); toast('Sztornó szállítólevél létrehozva.', 'success'); setShowDetail(null); loadNotes(); } catch (e: any) { toast(e.message || 'Hiba.', 'error'); }
                }}>Sztornó</Button>
              )}
              <Button variant="ghost" onClick={() => setShowDetail(null)}>Bezárás</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Szállítólevél készítés megrendelésből (a szállítólevelek oldalon belül) ──

interface OrdLine {
  id: string; description: string; ordered: number; delivered: number; remaining: number; unit: string; selected: boolean; deliverQty: number;
}

function DnFromOrderModal({ onClose, onSaved }: { onClose: () => void; onSaved: (issued?: { id: string; number: string; partnerId: string; partnerName: string }) => void }) {
  const { toast } = useToast();
  const [orders, setOrders] = useState<any[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [lines, setLines] = useState<OrdLine[]>([]);
  const [deliveryDate, setDeliveryDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingLines, setLoadingLines] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const profile = await get<any>('/company-profile');
        if (profile?.default_delivery_note_notes) setNotes(profile.default_delivery_note_notes);
      } catch { /* */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const data = await get<any>('/sales-orders?status=confirmed');
        setOrders(Array.isArray(data) ? data : data?.data || []);
      } catch { /* */ }
    })();
  }, []);

  useEffect(() => {
    if (!selectedOrderId) { setLines([]); return; }
    setLoadingLines(true);
    (async () => {
      try {
        const data = await get<any>(`/sales-orders/${selectedOrderId}`);
        const arr = data.lines || data.line_items || [];
        setLines(arr.map((l: any) => {
          const ordered = Number(l.quantity); const delivered = Number(l.delivered_qty || 0); const remaining = ordered - delivered;
          return { id: l.id, description: l.item_name_snapshot || 'Tétel', ordered, delivered, remaining, unit: l.unit || 'db', selected: remaining > 0, deliverQty: remaining > 0 ? remaining : 0 };
        }));
      } catch { setLines([]); }
      setLoadingLines(false);
    })();
  }, [selectedOrderId]);

  function toggleAll(checked: boolean) { setLines(prev => prev.map(l => l.remaining > 0 ? { ...l, selected: checked, deliverQty: checked ? l.remaining : 0 } : l)); }
  function toggleLine(id: string) { setLines(prev => prev.map(l => l.id !== id || l.remaining <= 0 ? l : { ...l, selected: !l.selected, deliverQty: !l.selected ? l.remaining : 0 })); }

  const selectedLines = lines.filter(l => l.selected && l.deliverQty > 0);
  const allSel = lines.filter(l => l.remaining > 0).length > 0 && lines.filter(l => l.remaining > 0).every(l => l.selected);

  async function handleSave() {
    if (!selectedOrderId) { toast('Válassz megrendelést.', 'error'); return; }
    if (selectedLines.length === 0) { toast('Válassz ki legalább egy tételt.', 'error'); return; }
    setSaving(true);
    try {
      const result = await post<any>(`/sales-orders/${selectedOrderId}/create-delivery-note`, {
        deliveryDate, notes: notes || undefined,
        lines: selectedLines.map(l => ({ salesOrderLineId: l.id, quantity: l.deliverQty })),
      });
      const selectedOrder = orders.find((o: any) => o.id === selectedOrderId);
      const pName = selectedOrder?.partner_name || selectedOrder?.partner_snapshot_json?.name || '';
      onSaved({
        id: result.id,
        number: result.delivery_note_number || '',
        partnerId: result.partner_id || '',
        partnerName: pName,
      });
    } catch (e: any) { toast(e.message || 'Mentés sikertelen.', 'error'); } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={true} onClose={onClose} title="Új szállítólevél" size="lg">
      <div className="space-y-4" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        <div>
          <label className="form-label">Megrendelés *</label>
          <select value={selectedOrderId} onChange={e => setSelectedOrderId(e.target.value)} className="form-input">
            <option value="">Válassz megrendelést...</option>
            {orders.map((o: any) => (
              <option key={o.id} value={o.id}>{(o.order_number || '').toUpperCase()} — {o.partner_name || o.partner_snapshot_json?.name || '—'}</option>
            ))}
          </select>
        </div>

        <Input label="Szállítás dátuma *" type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} />

        {loadingLines && <div style={{ padding: '20px', textAlign: 'center', color: 'var(--slate)' }}>Tételek betöltése...</div>}

        {lines.length > 0 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label className="form-label" style={{ margin: 0 }}>Tételek kiválasztása</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: 500 }}>
                <input type="checkbox" checked={allSel} onChange={e => toggleAll(e.target.checked)} style={{ width: '16px', height: '16px', accentColor: 'var(--navy)' }} />
                Összes
              </label>
            </div>
            <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {lines.map((l, i) => {
                const done = l.remaining <= 0;
                return (
                  <div key={l.id} style={{
                    display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                    borderTop: i > 0 ? '1px solid var(--line-2)' : 'none',
                    background: done ? 'var(--white)' : l.selected ? 'var(--mist)' : 'var(--white)',
                    opacity: done ? 0.4 : 1, padding: '10px 12px',
                  }}>
                    <div style={{ flexShrink: 0 }}>
                      {!done ? <input type="checkbox" checked={l.selected} onChange={() => toggleLine(l.id)} style={{ width: '18px', height: '18px', accentColor: 'var(--navy)', cursor: 'pointer' }} /> : <span title="Kész">✅</span>}
                    </div>
                    <div style={{ flex: 1, minWidth: '120px', cursor: done ? 'default' : 'pointer' }} onClick={() => !done && toggleLine(l.id)}>
                      <div style={{ fontWeight: 600, fontSize: '13px', color: l.selected && !done ? 'var(--navy)' : 'var(--slate)' }}>{l.description}</div>
                      <div style={{ fontSize: '11px', color: 'var(--slate)', marginTop: '2px' }}>
                        Rendelve: {l.ordered} · Szállítva: {l.delivered} · Hátralék: <b>{done ? 0 : l.remaining}</b> {l.unit}
                      </div>
                    </div>
                    {!done && l.selected && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                        <NumInput value={l.deliverQty}
                          onChange={v => setLines(prev => prev.map(x => x.id === l.id ? { ...x, deliverQty: v } : x))}
                          min={0.01} max={l.remaining} step="any"
                          className="form-input" style={{ width: '70px', textAlign: 'center', padding: '6px', fontSize: '14px', fontWeight: 600 }} />
                        <span style={{ fontSize: '12px', color: 'var(--slate)' }}>{l.unit}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--slate)', marginTop: '6px' }}>{selectedLines.length} tétel kiválasztva</div>
          </div>
        )}

        <div>
          <label className="form-label">Megjegyzés</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="form-input" placeholder="Szállítólevélhez fűzött megjegyzés..." />
        </div>

      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '12px', borderTop: '1px solid var(--line)', marginTop: '12px' }}>
        <div style={{ fontSize: '12px', color: 'var(--slate)' }}>{selectedLines.length > 0 && `${selectedLines.length} tétel`}</div>
        <div style={{ display: 'flex', gap: '9px' }}>
          <Button variant="ghost" onClick={onClose}>Mégse</Button>
          <Button onClick={handleSave} loading={saving} disabled={selectedLines.length === 0}>Szállítólevél létrehozása</Button>
        </div>
      </div>
    </Modal>
  );
}
